import { cn } from '@/lib/utils';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

const base =
  'inline-flex items-center justify-center gap-2 rounded-lg font-semibold transition-colors duration-150 ' +
  'disabled:opacity-40 disabled:cursor-not-allowed ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2';

/**
 * Primärknappen är mörk, inte färgad. Cyanen är El-kretsens accent och sparas
 * till länkar, aktivt läge och markeringar — hade varje knapp burit den hade
 * den slutat betyda något.
 */
const variants: Record<ButtonVariant, string> = {
  primary: 'bg-ink-900 text-surface hover:bg-ink-800 shadow-card',
  secondary: 'bg-surface text-ink-700 border border-ink-200 shadow-card hover:bg-ink-50 hover:border-ink-300',
  ghost: 'bg-transparent text-ink-600 hover:bg-ink-100 hover:text-ink-900',
  danger: 'bg-danger text-white hover:bg-danger-hover shadow-card',
};

const sizes: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-[13px]',
  md: 'h-9 px-3.5 text-[13.5px]',
  lg: 'h-11 px-5 text-[14.5px]',
};

/**
 * Knappens utseende utan knappens semantik.
 *
 * För en genväg som NAVIGERAR ska elementet vara ett `<a>` — annars tappas
 * ctrl-klick, högerklick och "öppna i ny flik", och skärmläsaren säger
 * "knapp" om något som byter sida.
 *
 * Ligger i egen fil och inte i Button.tsx: en fil som exporterar både en
 * komponent och annat bryter Fast Refresh, så en ändring i knappen skulle
 * ladda om hela sidan i stället för att uppdatera komponenten.
 */
export function buttonClasses(opts: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  className?: string;
} = {}): string {
  const { variant = 'primary', size = 'md', fullWidth, className } = opts;
  return cn(base, variants[variant], sizes[size], fullWidth && 'w-full', className);
}
